import { FC } from 'react'
import { Report } from 'common/Report/Report'
import { makeStyles, Grid } from '@material-ui/core'
import { WithReportLoader } from 'common/WithLoader/WithLoader'
import {
  useUpdateTransitionMaintenanceReportNotesMutation,
  useGetTransitionMaintenanceReportQuery
} from 'services/api/transitionApi'
import { useTransitionId } from 'context/TransitionContext'
import { Gauge, gaugeClasses, GaugeFormatterParams } from '@mui/x-charts/Gauge'
import { GridGrow } from 'common/Grid/GridGrow'

const useStyles = makeStyles(() => ({
  dropdown: {
    height: 30,
    width: 160
  },
  title: {
    marginLeft: 10
  },
  gaugeLabel: {
    position: 'absolute',
    marginTop: -5
  }
}))

export const MaintenanceReport: FC = () => {
  const classes = useStyles()
  const transitionId = useTransitionId()
  const { data: maintenanceReport, isLoading } = useGetTransitionMaintenanceReportQuery(transitionId)
  const [updateNotes, { originalArgs }] = useUpdateTransitionMaintenanceReportNotesMutation()
  const notes = originalArgs?.notes ?? maintenanceReport?.maintenanceNotes ?? ''
  const wpOpenCount = maintenanceReport?.assetReport?.wpOpenCount ?? 0
  const wpClosedCount = maintenanceReport?.assetReport?.wpClosedCount ?? 0
  const woOpenCount = maintenanceReport?.assetReport?.woOpenCount ?? 0
  const woClosedCount = maintenanceReport?.assetReport?.woClosedCount ?? 0
  const missingDocumentCount = maintenanceReport?.assetReport?.missingDocumentCount ?? 0

  const gaugeSettings = {
    height: 160,
    width: 350,
    startAngle: -90,
    endAngle: 90,
    innerRadius: '55%',
    outerRadius: '100%',
    text: (params: GaugeFormatterParams) => {
      return params.value != null ? `${params.value} / ${params.valueMax}` : ''
    },
    sx: () => ({
      [`& .${gaugeClasses.valueArc}`]: {
        fill: '#9929BD'
      },
      [`& .${gaugeClasses.valueText}`]: {
        transform: 'translate(0px, -8px)'
      }
    })
  }

  const missingDocumentGaugeSettings = {
    height: 160,
    startAngle: -90,
    endAngle: 90,
    innerRadius: '55%',
    outerRadius: '100%',
    text: (params: GaugeFormatterParams) => {
      return params.value != null ? `${params.value} / ${params.valueMax}` : ''
    },
    sx: () => ({
      [`& .${gaugeClasses.valueArc}`]: {
        fill: '#ff9f24'
      },
      [`& .${gaugeClasses.valueText}`]: {
        transform: 'translate(0px, -8px)'
      }
    })
  }

  function GetKpiPercentage (openItems: number, allItems: number) {
    if (allItems === 0) {
      return 0
    }

    return Math.round(openItems * 10000 / allItems) / 100
  }

  return (
    <Report
      title={'Maintenance Status'}
      info={'Status for work packages, work orders and missing documents.'}
      chartHeight={200}
      reportNote={{
        note: notes,
        saveNote: (notes) => updateNotes({ transitionId, notes })
      }}>
      <WithReportLoader
        noWrapper
        loading={isLoading}
        hasResults={true}>
        <GridGrow container spacing={2} direction="row">
          <Grid item sm={4} md={4}>
            <h3 className={classes.gaugeLabel}>
            WORK PACKAGES<br/>
              { GetKpiPercentage(wpClosedCount, wpOpenCount + wpClosedCount) }%
            </h3>
            <Gauge
              {...gaugeSettings}
              value={ wpClosedCount }
              valueMax={ wpOpenCount + wpClosedCount }
            >
            </Gauge>
          </Grid>
          <Grid item sm={4} md={4}>
            <h3 className={classes.gaugeLabel}>
            WORK ORDERS<br/>
              { GetKpiPercentage(woClosedCount, woOpenCount + woClosedCount) }%
            </h3>
            <Gauge
              {...gaugeSettings}
              value={ woClosedCount }
              valueMax={ woOpenCount + woClosedCount }
            >
            </Gauge>
          </Grid>
          <Grid item sm={4} md={4}>
            <h3 className={classes.gaugeLabel}>
            MISSING DOCUMENTS<br/>
              { GetKpiPercentage(missingDocumentCount, woOpenCount + woClosedCount) }%
            </h3>
            <Gauge
              {...missingDocumentGaugeSettings}
              value={ missingDocumentCount}
              valueMax={ woOpenCount + woClosedCount }
            >
            </Gauge>
          </Grid>
        </GridGrow>
      </WithReportLoader>
    </Report>
  )
}
